import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '-';
    const p = new Date(d);
    if (isNaN(p)) return '-';
    return `${String(p.getDate()).padStart(2, '0')}/${String(p.getMonth() + 1).padStart(2, '0')}/${p.getFullYear()}`;
};

const PRIMARY     = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT  = '#e0f7fa';

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const BUCKETS = [
    { key: 'Age_0_30',    label: '0-30',  bg: '#dcfce7', color: '#166534', hdr: '#166534' },
    { key: 'Age_31_60',   label: '31-60', bg: '#fef9c3', color: '#854d0e', hdr: '#854d0e' },
    { key: 'Age_61_90',   label: '61-90', bg: '#fed7aa', color: '#9a3412', hdr: '#9a3412' },
    { key: 'Age_90_Plus', label: '90+',   bg: '#fee2e2', color: '#991b1b', hdr: '#991b1b' },
];

const VendorAgeingFIFO = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [vendors, setVendors]   = useState([]);
    const [masterId, setMasterId] = useState('');
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && token) {
            fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token)
                .then(res => setVendors(res?.response_data || res || []))
                .catch(() => setVendors([]));
        }
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && masterId) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [masterId]);

    const fetchReport = async () => {
        if (!masterId) { setError('Please select a vendor'); return; }
        setLoading(true);
        try {
            const res = await fetchData('PurchaseReportApi', `/VendorAgeingFIFO/${companyId}/${branchId}/${masterId}`, token);
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vendor ageing FIFO report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.BillNo || '').toLowerCase().includes(q) ||
            (r.MasterName || '').toLowerCase().includes(q) ||
            (r.BillStatus || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        BillAmount:    acc.BillAmount    + (parseFloat(r.BillAmount)    || 0),
        PaidAmount:    acc.PaidAmount    + (parseFloat(r.PaidAmount)    || 0),
        BalanceAmount: acc.BalanceAmount + (parseFloat(r.BalanceAmount) || 0),
        Age_0_30:      acc.Age_0_30      + (parseFloat(r.Age_0_30)      || 0),
        Age_31_60:     acc.Age_31_60     + (parseFloat(r.Age_31_60)     || 0),
        Age_61_90:     acc.Age_61_90     + (parseFloat(r.Age_61_90)     || 0),
        Age_90_Plus:   acc.Age_90_Plus   + (parseFloat(r.Age_90_Plus)   || 0),
    }), { BillAmount: 0, PaidAmount: 0, BalanceAmount: 0, Age_0_30: 0, Age_31_60: 0, Age_61_90: 0, Age_90_Plus: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'VendorAgeingFIFO', 0);
        if (!filtered.length) return;
        const vendorName = vendors.find(v => String(v.MasterId || v.Id) === String(masterId))?.Name || '';
        const headers = ['#', 'Purchase ID', 'Bill No', 'Bill Date', 'Supplier',
            'Bill Amount', 'Paid Amount', 'Balance', 'Bill Status', 'Age Days', '0-30', '31-60', '61-90', '90+'];
        const rows = filtered.map((r, i) => [
            i + 1, r.PurchaseId, r.BillNo, fmtDate(r.BillDate), r.MasterName,
            r.BillAmount, r.PaidAmount, r.BalanceAmount, r.BillStatus, r.AgeDays,
            r.Age_0_30, r.Age_31_60, r.Age_61_90, r.Age_90_Plus,
        ]);

        await exportStyledExcel({
            reportTitle: 'VENDOR AGEING ANALYSIS - FIFO BASED',
            sheetName: 'Vendor Ageing FIFO',
            fileName: `VendorAgeingFIFO_${vendorName}.xlsx`,
            companyName,
            username,
            headers: headers,
            rows,
            totalsRow: ['', '', '', '', 'Totals',
                fmtN(totals.BillAmount), fmtN(totals.PaidAmount), fmtN(totals.BalanceAmount), '', '',
                fmtN(totals.Age_0_30), fmtN(totals.Age_31_60), fmtN(totals.Age_61_90), fmtN(totals.Age_90_Plus)],
            columnWidths: [4, 10, 14, 12, 24, 14, 14, 14, 12, 10, 12, 12, 12, 12],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #80cbc4', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading vendor ageing FIFO report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Vendor Ageing Analysis
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>FIFO Based</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input placeholder="Search bill / vendor..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 160, outline: 'none' }} />
                    <SearchableDropdown
                        value={masterId}
                        onChange={v => setMasterId(v)}
                        options={vendors.map(v => ({ value: String(v.MasterId || v.Id), label: v.Name }))}
                        placeholder="Select Vendor"
                        minWidth={180}
                        primaryColor={PRIMARY}
                    />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Bucket summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {BUCKETS.map(b => (
                            <div key={b.key} style={{ background: b.bg, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 }}>
                                <span style={{ fontSize: 10, color: b.color, fontWeight: 600 }}>{b.label} Days</span>
                                <span style={{ fontSize: 12, color: b.color, fontWeight: 700 }}>{fmtN(totals[b.key])}</span>
                            </div>
                        ))}
                        <div style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 }}>
                            <span style={{ fontSize: 10, color: PRIMARY, fontWeight: 600 }}>Balance Total</span>
                            <span style={{ fontSize: 12, color: PRIMARY, fontWeight: 700 }}>{fmtN(totals.BalanceAmount)}</span>
                        </div>
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {filtered.length} bills{search ? ` (filtered from ${data.length})` : ''}
                        &nbsp;|&nbsp; Bill Amt: <strong style={{ color: PRIMARY }}>{fmtN(totals.BillAmount)}</strong>
                        &nbsp;|&nbsp; Balance: <strong style={{ color: '#b91c1c' }}>{fmtN(totals.BalanceAmount)}</strong>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 900, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Supplier</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amount</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#0a5f6b' }}>Balance</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Status</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Age Days</th>
                                {BUCKETS.map(b => (
                                    <th key={b.key} style={{ ...thBase, textAlign: 'right', background: b.hdr }}>{b.label} Days</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={13} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {!masterId ? 'Select a vendor to load report' : fetched ? 'No records found' : 'Select a vendor and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const ageDays = r.AgeDays || 0;
                                const ageColor = ageDays > 90 ? '#991b1b' : ageDays > 60 ? '#9a3412' : ageDays > 30 ? '#854d0e' : '#166534';
                                const rowBg = i % 2 === 0 ? '#fff' : PRIMARY_LT;
                                return (
                                    <tr key={`${r.PurchaseId}-${i}`} style={{ background: rowBg }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                                        <td style={{ ...tdBase, fontWeight: 500 }}>{r.MasterName || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.BillAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtN(r.PaidAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fff5f5' }}>{fmtN(r.BalanceAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'center' }}>
                                            {r.BillStatus ? (
                                                <span style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: r.BillStatus === 'Fully Paid' ? '#d1fae5' : '#fef9c3', color: r.BillStatus === 'Fully Paid' ? '#059669' : '#92400e' }}>
                                                    {r.BillStatus}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td style={{ ...tdBase, textAlign: 'center', fontWeight: 700, color: ageColor }}>{ageDays}</td>
                                        {BUCKETS.map(b => {
                                            const val = parseFloat(r[b.key]) || 0;
                                            return (
                                                <td key={b.key} style={{ ...tdBase, textAlign: 'right', background: val ? b.bg : 'transparent', color: val ? b.color : '#cbd5e1', fontWeight: val ? 600 : 400 }}>
                                                    {val ? fmtN(val) : '-'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={4} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(totals.BillAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(totals.PaidAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(totals.BalanceAmount)}</td>
                                    <td colSpan={2} style={{ ...tdBase }} />
                                    {BUCKETS.map(b => (
                                        <td key={b.key} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: b.color, background: b.bg }}>{fmtN(totals[b.key])}</td>
                                    ))}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default VendorAgeingFIFO;

